<template>
  <div class="sp-upload">
    <div
      class="sp-drop"
      :class="{ 'sp-drop-active': dragging, 'sp-drop-busy': uploading }"
      @dragenter.prevent="dragging = true"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop.prevent="onDrop"
    >
      <div class="sp-drop-label">HubSpot Deals export</div>
      <div v-if="uploading" class="sp-drop-hint">
        <b-spinner small label="Uploading"></b-spinner>
        <span class="ml-2">Uploading {{ fileName }}…</span>
      </div>
      <div v-else class="sp-drop-hint">
        Drop an .xlsx or .csv file here, or
        <button type="button" class="btn btn-link btn-sm p-0 align-baseline" @click="browse">browse</button>
      </div>
      <input
        ref="input"
        type="file"
        class="d-none"
        :accept="accept"
        aria-label="Upload HubSpot Deals export"
        @change="onPick"
      />
    </div>

    <div v-if="error" class="sp-slot-error" role="alert">
      <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>{{ error }}
    </div>

    <div v-else-if="result" class="sp-slot-result" role="status">
      <div>
        <strong>{{ result.fileName }}</strong>: {{ formatCount(result.inserted) }} inserted,
        {{ formatCount(result.updated) }} updated, {{ formatCount(result.rejected) }} rejected.
      </div>
      <div v-if="result.warnings.length" class="sp-slot-warning">
        <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
        {{ result.warnings.join(" ") }}
      </div>
      <div v-if="result.uncategorisedLostReasons" class="sp-slot-warning">
        <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
        {{ formatCount(result.uncategorisedLostReasons) }} Closed Lost
        {{ result.uncategorisedLostReasons === 1 ? "deal" : "deals" }} couldn't be categorised. The next upload will
        try again.
      </div>
      <details v-if="result.rejectedRows.length" class="sp-rejected">
        <summary>
          Rejected rows
          <template v-if="result.rejected > result.rejectedRows.length">
            (first {{ result.rejectedRows.length }} of {{ formatCount(result.rejected) }})
          </template>
        </summary>
        <table class="table table-sm mb-0">
          <thead>
            <tr>
              <th scope="col">Row</th>
              <th scope="col">Record ID</th>
              <th scope="col">Reason</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in result.rejectedRows" :key="r.row">
              <td>{{ r.row }}</td>
              <td>{{ r.recordId || "–" }}</td>
              <td>{{ r.reason }}</td>
            </tr>
          </tbody>
        </table>
      </details>
    </div>
  </div>
</template>

<script>
import { uploadHubSpotExport } from "@/services/businessAnalyticsService";
import { formatCount } from "./chartTheme";
import { UPLOAD_EXTENSIONS, fileProblem, uploadErrorMessage } from "./salesPipeline";

/**
 * Sales Pipeline upload panel for the HubSpot Deals export. Shows the upload's counts, warnings and
 * rejected rows, or its error. Emits `uploaded` after a successful upload so the section reloads
 * its figures.
 */
export default {
  name: "HubSpotUploadPanel",
  data() {
    return {
      accept: UPLOAD_EXTENSIONS.join(","),
      dragging: false,
      uploading: false,
      fileName: "",
      result: null,
      error: "",
    };
  },
  methods: {
    formatCount,
    browse() {
      if (!this.uploading) this.$refs.input.click();
    },
    onPick(event) {
      const file = event.target.files && event.target.files[0];
      // Clear it so picking the same file again still fires `change`.
      event.target.value = "";
      if (file) this.upload(file);
    },
    onDrop(event) {
      this.dragging = false;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (file && !this.uploading) this.upload(file);
    },
    async upload(file) {
      this.result = null;
      this.error = fileProblem(file);
      if (this.error) return;
      this.uploading = true;
      this.fileName = file.name;
      try {
        this.result = await uploadHubSpotExport(file);
        this.$emit("uploaded");
      } catch (err) {
        this.error = uploadErrorMessage(err);
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.sp-drop {
  border: 2px dashed rgba(128, 128, 128, 0.4);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}
.sp-drop-active {
  border-color: #6eb644;
  background: rgba(110, 182, 68, 0.08);
}
.sp-drop-busy {
  opacity: 0.8;
}
.sp-drop-label {
  font-weight: 600;
  margin-bottom: 4px;
}
.sp-drop-hint {
  font-size: 13px;
  opacity: 0.85;
}
.sp-slot-error {
  margin-top: 8px;
  font-size: 13px;
  color: #c0392b;
}
.sp-slot-result {
  margin-top: 8px;
  font-size: 13px;
}
.sp-slot-warning {
  color: #b9770e;
}
.sp-rejected {
  margin-top: 4px;
}
.sp-rejected summary {
  cursor: pointer;
}
.sp-rejected table {
  font-size: 12px;
}
</style>
